<script setup lang="ts">
import { SongLeadVocalDataResponse } from "@/models/SongLeadVocalDataResponse";
import { LoadStatus } from "@/models/frontend-only/LoadStatus";
import { NoAuthReason } from "@/models/NoAuthReason";

const store = useSongStore();
const midiStore = useMidiStore();
const busy = useBusyStore();
const pdf = usePDFStore();
const api = useApiStore();
const profile = useProfileStore();
const toast = useToastStore();
const shareStore = useShareStore();
const betaStore = useBetaStore();

const props = defineProps<{
  label: string;
  getSongPdfEndpoint: string;
  getSongPreviewEndpoint: string;
  placeholderImg: string;
  noAuthReason: NoAuthReason | null;
  allowPreview: boolean;
  exceededMaxUniqueSongCount: boolean;
  translatedTitle: string;
}>();

const defaultKey = computed(() => store.defaultKey || "");
const songNumber = computed(() => store.data?.ccliSongNumber || "");
const status = ref<LoadStatus>(LoadStatus.Loading);
const previewData = ref<SongLeadVocalDataResponse | null>(null);
const imgs = computed(() => previewData.value?.svgs || []);
const midi = computed(() => previewData.value?.midi);
const timemap = computed(() => previewData.value?.timemap);
const defaultBpm = computed(() => previewData.value?.defaultBpm || null);
const fullMidiController = ref(false);
const key = ref<string>(defaultKey.value);
const octave = ref<string>("0");
const noteSize = ref<string>("0");
const orientation = ref("portrait");
const paperSize = ref("Letter");
const renderer = betaStore.features.sheetmusicPipeline ? "pipeline" : "legacy";

const showBusy = ref({
  copy: false,
  print: false,
  download: false,
  share: false,
});

const shownBusy = computed(() => ({
  email: store.emailSettings.busy,
  ...showBusy.value,
}));

const labelLowerCase = computed(() => props.label.toLowerCase());

function usedMaxSongs(product: string) {
  if (product === "lead") {
    return props.exceededMaxUniqueSongCount;
  } else if (product === "vocal") {
    return props.exceededMaxUniqueSongCount;
  }
  return false;
}

const filename = computed(
  () =>
    sanitizeFilename(store.song?.title || "") +
    "-" +
    props.label.toLowerCase() +
    "-" +
    key.value,
);

function reset() {
  octave.value = "0";
  noteSize.value = "0";
  orientation.value = "portrait";
  paperSize.value = profile.profile?.paperSize || "Letter";
  key.value = defaultKey.value;
}

function getPdfUrl(activityType: "shared" | "printed" | "downloaded") {
  const searchParams = new URLSearchParams({
    songNumber: songNumber.value,
    transposeKey: key.value,
    octave: octave.value,
    noteSize: noteSize.value,
    orientation: orientation.value,
    paperSize: paperSize.value,
    activityType: activityType,
    renderer: renderer,
  });
  const url = `/api${props.getSongPdfEndpoint}?${searchParams.toString()}`;
  return url;
}

async function print() {
  if (!store.song) {
    return;
  }
  showBusy.value.print = true;
  const url = getPdfUrl("printed");
  try {
    await pdf.printPdf(url);
  } catch (err) {
    toast.errorToast("SV.LIT_PrintErrorHasOccurred");
    throw err;
  }
  showBusy.value.print = false;
}

async function download() {
  if (!songNumber.value || !store.song) {
    return;
  }
  showBusy.value.download = true;

  await downloadUrl(getPdfUrl("downloaded"), filename.value + ".pdf");
  showBusy.value.download = false;
}

async function share() {
  if (!store.song) {
    return;
  }
  showBusy.value.share = true;

  await shareStore.fetchUrlPdfAndShare(
    getPdfUrl("shared"),
    filename.value + ".pdf",
    store.song.title,
    props.translatedTitle,
  );
  showBusy.value.share = false;
}

const init = ref(false);

async function initWhenSongIsLoaded() {
  if (!store.defaultKey || !profile.profile) {
    window.setTimeout(initWhenSongIsLoaded, 100);
  } else {
    reset();
    await getSongSheet(false);
    init.value = true;
  }
}

onMounted(() => initWhenSongIsLoaded());

watch(songNumber, () => getSongSheet(false));
watch(key, () => {
  if (init.value) {
    getSongSheet(true);
  }
});
watch(octave, () => {
  if (init.value) {
    getSongSheet(true);
  }
});
watch(noteSize, () => {
  if (init.value) {
    getSongSheet(true);
  }
});
watch(orientation, () => {
  if (init.value) {
    getSongSheet(true);
  }
});
watch(paperSize, () => {
  if (init.value) {
    getSongSheet(true);
  }
});

watch(
  () => midiStore.fullMidiController,
  () => (fullMidiController.value = midiStore.fullMidiController),
);

async function getSongSheet(reload: boolean) {
  const startTime = midiStore.progressTime;
  const playing = midiStore.playing;
  const bpm = midiStore.bpm;
  if (!props.allowPreview) {
    return;
  }

  midiStore.loadInstrument();

  if (!songNumber.value || !key.value) {
    return;
  }

  busy.showBusy();
  status.value = LoadStatus.Loading;
  try {
    previewData.value = (await api.get(props.getSongPreviewEndpoint, {
      songNumber: songNumber.value,
      transposeKey: key.value,
      octave: octave.value,
      noteSize: noteSize.value,
      orientation: orientation.value,
      paperSize: paperSize.value,
      renderer: renderer,
    })) as SongLeadVocalDataResponse;
    status.value = LoadStatus.Ready;
  } catch (err) {
    status.value = LoadStatus.Error;
    throw err;
  }
  busy.hideBusy();
  if (midiStore.ready && reload) {
    midiStore.clearSong();
    await midiStore.loadSong();
    midiStore.skipToSeconds(startTime);

    setTimeout(() => {
      if (!playing) {
        midiStore.togglePlay();
      }

      if (bpm !== midiStore.bpm) {
        midiStore.setBpm(bpm);
      }
    }, 10);
  }
}

function onEmail() {
  store.emailSong({
    product: props.label.toLowerCase(),
    songKey: key.value,
    octave: octave.value,
    noteSize: noteSize.value,
    orientation: orientation.value,
    paperSize: paperSize.value,
    renderer: renderer,
    style: null,
    columns: null,
  });
}
</script>

<template>
  <PageWrapper no-body>
    <FullScreenWrapper>
      <template #header>
        <SongDetailsAlertContentError
          v-if="status === LoadStatus.Error"
          class="mt-2"
          @btnclick="getSongSheet(true)"
        />
        <SongDetailsAlertNotAuthenticated
          v-else-if="
            !store.placeholderNotLoaded &&
            noAuthReason === NoAuthReason.NotAuthenticated
          "
          class="mt-2"
        />
        <SongDetailsAlertUpgradeSubscription
          v-else-if="
            !store.placeholderNotLoaded &&
            noAuthReason === NoAuthReason.RequiresSubscriptionUpgrade
          "
          class="mt-2"
        />
      </template>

      <MusicSheetSvgs
        :imgs="imgs"
        :show-placeholder="!previewData?.svgs"
      />

      <template #toolbar>
        <div class="flex flex-wrap items-center gap-1">
          <div
            v-if="!fullMidiController"
            class="flex items-center gap-1"
          >
            <ToolbarSelectChordsTransposeKey
              :id="`${label}SheetTransposeKeySelect`"
              v-model="key"
              :default-key="defaultKey"
            />

            <ToolbarSelectOctave
              :id="`${label}SheetOctaveSelect`"
              v-model="octave"
            />

            <ToolbarSelectNoteSize
              :id="`${label}SheetNoteSizeSelect`"
              v-model="noteSize"
              :content-type="label"
            />
          </div>
          <div class="flex items-center gap-1">
            <ToolbarSelectOrientation
              v-if="!fullMidiController"
              :id="`${label}SheetOrientation`"
              v-model="orientation"
            />
            <ToolbarSelectPaperSize
              v-if="!fullMidiController"
              :id="`${label}SheetPaperSizeSelect`"
              v-model="paperSize"
            />
            <ToolbarSeparator />

            <ToolbarMidiPlayback
              :id="`${label}SheetMidiPlayback`"
              :timemap="timemap || []"
              :midi="midi || ''"
              :default-bpm="defaultBpm"
              :sheet-type="`${label}Sheet`"
              :allow-preview="allowPreview"
            />
          </div>
        </div>
      </template>

      <template #action-bar-controls>
        <div
          class="ga-info"
          :data-songnumber="store.song?.ccliSongNumber"
          :data-key="key"
          :data-octave="octave"
          :data-tempo="midiStore.bpm"
          :data-note-size="noteSize"
          :data-orientation="orientation"
          :data-paper-size="paperSize"
          :data-content-type="props.label + 'Sheet'"
        >
          <ToolbarActionBarControls
            enable-email
            enable-share
            :enable-print="!isFirefox()"
            :product="label.toLowerCase()"
            :max-song-usage-check="usedMaxSongs"
            :show-busy="shownBusy"
            :ids="{
              copy: `${labelLowerCase}SheetCopyButton`,
              download: `${labelLowerCase}SheetDownloadButton`,
              email: `${labelLowerCase}SheetEmailButton`,
              fullscreen: `${labelLowerCase}SheetFullscreenButton`,
              print: `${labelLowerCase}SheetPrintButton`,
              share: `${labelLowerCase}SheetShareButton`,
            }"
            @print="print()"
            @share="share()"
            @download="download()"
            @email="onEmail"
          />
        </div>
      </template>
    </FullScreenWrapper>
  </PageWrapper>
</template>

<style scoped lang="postcss"></style>
